import { Clickable } from '@carvana/merch-components';
import { ReactEventHandler, useContext } from 'react';
import Portal from '@/common/Portal';
import FullScreenGallery from '@/vdp/Gallery/Carousel/FullScreenGallery';
import GalleryImageType from '~/constants/vdp/galleryImageType';
import { useDeviceType } from '~/context/common/DeviceContext';
import CarouselContext from '~/context/vdp/CarouselContext';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { logError } from '~/utilities/errorHandlers';
import { GalleryCta } from './LiteGallery.styles';
import useImages from './useImages';

type OwnProps = {
  handleOpenSpinner: () => void;
  setHeroImageFailed: (value: boolean) => void;
};

const GalleryImages = ({ handleOpenSpinner, setHeroImageFailed }: OwnProps) => {
  const { isFullScreen, openFullScreen } = useContext(CarouselContext);
  const { bottomImage, hero, initialImageId, remainingImagesLength, topImage } =
    useImages();
  const { isDesktop } = useDeviceType();

  const handleImageError: ReactEventHandler<HTMLImageElement> = e => {
    setHeroImageFailed(true);
    logError('Gallery Image Error', {
      name: e.currentTarget.alt,
      imageUrl: e.currentTarget.src,
      location: 'Lite Gallery'
    });
  };

  return (
    <>
      <div className="relative">
        <Clickable
          className="h-full"
          data-analytics-id="Gallery Exterior"
          onClick={() => openFullScreen(hero.id, hero.type)}
        >
          <img
            className="gallery-image"
            src={hero.url}
            alt="exterior"
            onError={handleImageError}
          />
        </Clickable>
        <Clickable
          element={GalleryCta}
          data-analytics-id="Gallery 360 Tour"
          onClick={handleOpenSpinner}
        >
          360° Tour
        </Clickable>
      </div>
      <Clickable
        data-analytics-id="Gallery First Interior"
        onClick={() => openFullScreen(topImage.id, topImage.type)}
      >
        <img
          className="gallery-image"
          src={topImage.url}
          alt="interior"
          onError={handleImageError}
        />
      </Clickable>
      <div className="relative">
        <Clickable
          className="h-full"
          data-analytics-id="Gallery Second Interior"
          onClick={() => openFullScreen(bottomImage.id, bottomImage.type)}
        >
          <img
            className="gallery-image"
            src={bottomImage.url}
            alt="interior"
            onError={handleImageError}
          />
        </Clickable>
        {!!remainingImagesLength && (
          <Clickable
            element={GalleryCta}
            data-analytics-id="Gallery Show All Photos"
            onClick={() => openFullScreen(initialImageId, GalleryImageType.All)}
          >
            +{remainingImagesLength} {isDesktop && 'Photos'}
          </Clickable>
        )}
      </div>
      <Portal id="fullscreen-gallery" open={isFullScreen}>
        <FullScreenGallery />
      </Portal>
    </>
  );
};

export default withErrorBoundary(GalleryImages);
